import { Download, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";

import { useDataExport } from "./use-data-export";

interface DataExportSectionProps {
  userId: string;
  onUnlockPrivacy: () => Promise<boolean>;
}

const STATUS_LABELS = {
  queued: "等待导出",
  processing: "正在导出",
  succeeded: "导出完成",
  failed: "导出失败",
  cancelled: "已取消",
} as const;

export function DataExportSection({ userId, onUnlockPrivacy }: DataExportSectionProps) {
  const {
    taskId,
    taskQuery,
    create,
    isCreating,
    cancel,
    isCancelling,
    download,
    isDownloading,
  } = useDataExport(userId, onUnlockPrivacy);
  const task = taskQuery.data;
  const active = task?.status === "queued" || task?.status === "processing";
  const progress = Math.min(100, Math.max(0, task?.progress_percent ?? 0));

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs leading-5 text-muted-foreground">
            导出全部收藏、文件夹、标签和附件，生成 ZIP 文件供你保存。
          </p>
        </div>
        {!taskId && (
          <Button type="button" size="sm" disabled={isCreating} onClick={create}>
            {isCreating && <Loader2 className="size-4 animate-spin" />}
            导出数据
          </Button>
        )}
      </div>

      {taskId && taskQuery.isPending && taskQuery.isFetching && (
        <p className="mt-4 text-xs text-muted-foreground">正在读取上次的导出任务…</p>
      )}

      {taskId && taskQuery.isError && !task && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">暂时无法获取导出任务。</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => void taskQuery.refetch()}>
              重试
            </Button>
            <Button type="button" size="sm" className="font-normal" disabled={isCreating} onClick={create}>
              重新导出
            </Button>
          </div>
        </div>
      )}

      {task && (
        <div className="mt-4">
          {taskQuery.isError && (
            <div className="mb-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>状态更新失败，显示的是上次获取的进度。</span>
              <button type="button" className="shrink-0 underline hover:text-foreground" onClick={() => void taskQuery.refetch()}>
                重试
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {task.status === "failed" ? "上次导出未完成，请重新尝试。" : STATUS_LABELS[task.status]}
              {active && <span className="ml-2 tabular-nums">{progress}%</span>}
            </p>
            <div className="flex flex-wrap gap-2">
              {active && (
                <Button type="button" variant="outline" size="sm" className="font-normal" disabled={isCancelling} onClick={cancel}>
                  {isCancelling && <Loader2 className="size-4 animate-spin" />}
                  取消导出
                </Button>
              )}
              {task.status === "succeeded" && (
                <Button type="button" size="sm" disabled={isDownloading} onClick={() => void download()}>
                  {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                  下载 ZIP
                </Button>
              )}
              {!active && (
                <Button type="button" variant="outline" size="sm" className="font-normal" disabled={isCreating} onClick={create}>
                  {isCreating && <Loader2 className="size-4 animate-spin" />}
                  重新导出
                </Button>
              )}
            </div>
          </div>
          {active && (
            <>
              <div
                className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-label="数据导出进度"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                导出会在后台继续进行，稍后可回到这里查看。
                {task.attempt_count > 1 && " 系统正在自动重试。"}
              </p>
            </>
          )}
          {task.status === "succeeded" && (
            <p className="mt-2 text-xs text-muted-foreground">下载时如需验证隐私密码，会先引导你解锁。</p>
          )}
        </div>
      )}
    </div>
  );
}
